import React from 'react'
import axios from 'axios';
import SortableItem from './SortableItem'

const REORDER_URL = "/admin/school_resources/reorder"

// One PATCH per drag gesture. react-sortable's HOC keeps its drag-end handler private and
// exposes only onSortItems, which fires on every swap while the pointer moves, so the save
// is debounced rather than bound to a drop event. This replaces an unconditional
// setInterval that PATCHed the reorder endpoint every 1.5s for as long as the tab stayed
// open, whether or not anything had changed.
const SAVE_DEBOUNCE_MS = 500

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

export default class SortableList extends React.Component {

  state = {
    items: this.props.items,
    error: null
  };

  // Monotonic; identifies the most recently issued save so an older response cannot
  // clear savePending on a newer, still-unacknowledged one.
  saveToken = 0;

  onSortItems = (items) => {
    this.setState({
      items: items
    });
    this.scheduleSave();
  }

  componentDidMount() {
    // Turbo Drive is disabled globally, so navigating away from this page is a full
    // browser load and React never unmounts - componentWillUnmount cannot be relied on to
    // flush a pending save. pagehide is the hook that actually fires, and keepalive lets
    // the request outlive the page.
    this.flushOnHide = () => { if (this.savePending) this.flushKeepalive(); };
    window.addEventListener('pagehide', this.flushOnHide);
  }

  componentWillUnmount() {
    clearTimeout(this.saveTimer);
    this.unmounted = true;
    window.removeEventListener('pagehide', this.flushOnHide);
    if (this.savePending) this.flushKeepalive();
  }

  scheduleSave = () => {
    this.savePending = true;
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(this.save, SAVE_DEBOUNCE_MS);
  }

  save = () => {
    // savePending stays TRUE until the server has accepted the CURRENT order. Clearing it
    // before the request resolves left a window where closing the tab mid-save dropped
    // the reorder silently - precisely what the pagehide flush exists to prevent.
    //
    // The token guards the overlapping case. scheduleSave only clears the debounce timer,
    // not an in-flight request, so a second drag can leave two PATCHes outstanding.
    // Without the token an older response landing last would clear savePending even
    // though the newer order had not been acknowledged, and a tab close straight after
    // would flush nothing.
    //
    // The endpoint requires an admin, and protect_from_forgery :null_session drops the
    // session on a token-less request, which would leave the controller with no
    // current_user and silently redirect instead of saving.
    const token = ++this.saveToken;

    axios.patch(
      REORDER_URL,
      { items: this.state.items },
      { headers: { 'X-CSRF-Token': csrfToken() } }
    ).then((response) => {
      // require_admin redirects to root_path instead of returning 401, and XHR follows
      // redirects transparently - so a REJECTED save arrives here as a 200 carrying the
      // home page's HTML. Only our own JSON payload counts as success; without this
      // check an expired session looks identical to a saved reorder.
      const saved = response.data && response.data.success === true;
      if (saved && token === this.saveToken) this.savePending = false;
      this.reportResult(saved ? null : 'Could not save the new order - your session may have expired. Reload the page and sign in again.');
    }).catch(() => {
      // Previously failures were invisible, so a rejected save looked like a saved one.
      this.reportResult('Could not save the new order. Reload the page and try again.');
    });
  }

  // Last-gasp save as the page goes away; no response is available to check.
  flushKeepalive = () => {
    this.savePending = false;
    fetch(REORDER_URL, {
      method: 'PATCH',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
      body: JSON.stringify({ items: this.state.items })
    }).catch(() => {});
  }

  reportResult = (error) => {
    if (!this.unmounted) this.setState({ error: error });
  }

  render() {
    const { items, error } = this.state;
    var listItems = items.map((item, i) => {
      return (
        <SortableItem
          key={i}
          onSortItems={this.onSortItems}
          items={items}
          sortId={i}
          item={item}
          />
      )
    })

    return (
      <div>
        {error && <div className='alert alert-danger'>{error}</div>}
        <table className='table'>
          <thead>
            <tr>
              <th>Category</th>
              <th>Link</th>
              <th>Form Link</th>
            </tr>
          </thead>
        <tbody>
          {listItems}
        </tbody>
        </table>
      </div>
    )
  }
}
